import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation, Navigate } from "react-router-dom";
import { Mail, KeyRound, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCustomer } from "@/lib/customer";
import ContestLogin from "@/pages/ContestLogin";
import { isCustomHost } from "@/lib/host";

const err = (e, fb) => {
  if (e?.request && !e?.response) return "Impossibile raggiungere il server: controlla la connessione e riprova";
  return e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || fb;
};
const verifyLabel = (busy, registered) => {
  if (busy) return "Verifica…";
  return registered ? "Accedi" : "Registrati e accedi";
};

export function CustomerLoginForm({ next = "/concorsi", contest = null }) {
  const navigate = useNavigate();
  const { login } = useCustomer();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [registered, setRegistered] = useState(true);
  const [profile, setProfile] = useState({ name: "", phone: "", marketing_consent: false });
  const [rules, setRules] = useState(false);
  const [busy, setBusy] = useState(false);
  const needsRules = Boolean(contest && (contest.rules_text || contest.rules_file_id || contest.privacy_text || contest.privacy_file_id));

  const requestOtp = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) return toast.error("Inserisci un'email valida");
    setBusy(true);
    try {
      const r = await requestOtpWithRetry(email.trim(), (n) => toast.message(`Server non raggiungibile, riprovo… (${n}/3)`, { id: "otp-retry" }));
      setRegistered(r.registered);
      setStep("otp");
      toast.success("Codice inviato via email");
    } catch (e) {
      toast.error(err(e, "Invio non riuscito"));
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    if (otp.length !== 6) return toast.error("Inserisci il codice a 6 cifre");
    if (!registered && !profile.name.trim()) return toast.error("Inserisci il tuo nome");
    if (!registered && needsRules && !rules) return toast.error("Accetta il regolamento e l'informativa privacy");
    setBusy(true);
    try {
      const extra = contest ? { contest_id: contest.id, rules_accepted: rules || !needsRules } : {};
      const r = await api.customerOtpVerify({ email: email.trim(), otp, ...(registered ? {} : profile), ...extra });
      login(r);
      toast.success(registered ? "Bentornato!" : "Registrazione completata");
      navigate(next, { replace: true });
    } catch (e) {
      toast.error(err(e, "Codice non valido"));
    } finally {
      setBusy(false);
    }
  };
  const google = () => {
    sessionStorage.setItem("vw_customer_next", next);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (contest ? window.location.pathname : "/accedi");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="space-y-4" data-testid="customer-login">
        <p className="text-xs text-muted-foreground">Senza password: ti inviamo un codice via email</p>
        {step === "email" ? (
          <>
            <div className="space-y-1.5">
              <Label>La tua email</Label>
              <Input data-testid="customer-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && requestOtp()} placeholder="nome@esempio.it" />
            </div>
            <Button data-testid="customer-otp-request" onClick={requestOtp} disabled={busy} className="w-full gap-2 h-11"><Mail className="h-4 w-4" /> {busy ? "Invio…" : "Inviami il codice"}</Button>
            <div className="relative text-center text-xs text-muted-foreground"><span className="bg-background px-2 relative z-10">oppure</span><div className="absolute inset-x-0 top-1/2 border-t border-border" /></div>
            <Button data-testid="customer-google-button" variant="outline" onClick={google} className="w-full gap-2 h-11 border-border">
              <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continua con Google
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm">Codice inviato a <b>{email}</b>. <button type="button" className="underline text-xs text-muted-foreground" onClick={() => setStep("email")}>Cambia email</button></p>
            <div className="space-y-1.5">
              <Label>Codice a 6 cifre</Label>
              <Input data-testid="customer-otp-input" required inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} className="font-mono-code text-center text-2xl tracking-[0.4em] h-14" />
            </div>
            {!registered && (
              <div className="space-y-3 rounded-lg bg-secondary/40 p-3" data-testid="customer-register-fields">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" /> Prima volta? Completa la registrazione</p>
                <Input data-testid="customer-name-input" required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Nome e cognome *" />
                <Input data-testid="customer-phone-input" type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Telefono WhatsApp (facoltativo)" />
                <label className="flex items-start gap-2 text-xs cursor-pointer"><input type="checkbox" data-testid="customer-consent-checkbox" checked={profile.marketing_consent} onChange={(e) => setProfile({ ...profile, marketing_consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-primary" /><span>Acconsento a ricevere comunicazioni promozionali (facoltativo)</span></label>
                {needsRules && <RulesConsent contest={contest} checked={rules} onChange={setRules} />}
              </div>
            )}
            <Button data-testid="customer-otp-verify" onClick={verify} disabled={busy} className="w-full gap-2 h-11"><KeyRound className="h-4 w-4" /> {verifyLabel(busy, registered)}</Button>
          </>
        )}
        {!contest && <button type="button" onClick={() => navigate("/concorsi")} className="w-full text-xs text-muted-foreground hover:text-primary underline" data-testid="customer-login-back">Guarda i concorsi senza accedere</button>}
    </div>
  );
}

export function RulesConsent({ contest, checked, onChange, testid = "customer-rules-checkbox" }) {
  const hasPrivacy = contest.privacy_text || contest.privacy_file_id;
  return (
    <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="customer-rules-label">
      <input type="checkbox" required data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
      <span>Ho letto e accetto il <a href={`/regolamento/${contest.id}`} target="_blank" rel="noreferrer" className="underline font-semibold">regolamento</a>{hasPrivacy && <> e l'<a href={`/privacy/${contest.id}`} target="_blank" rel="noreferrer" className="underline font-semibold">informativa privacy</a></>} del concorso <b>{contest.name}</b> *</span>
    </label>
  );
}

const isNetworkError = (e) => Boolean(e?.request && !e?.response);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Riprova da sola (fino a 3 tentativi) se il server non risponde, es. connessione instabile o server in risveglio.
const requestOtpWithRetry = async (email, onRetry) => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await api.customerOtpRequest(email);
    } catch (e) {
      if (!isNetworkError(e) || attempt >= 3) throw e;
      onRetry(attempt);
      await wait(2000 * attempt);
    }
  }
};

export default function CustomerLogin() {
  const [params] = useSearchParams();
  const next = params.get("next");
  if (isCustomHost()) return <ContestLogin hostMode />;
  return <Navigate to={`/login?tab=cliente${next ? `&next=${encodeURIComponent(next)}` : ""}`} replace />;
}

export function CustomerAuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useCustomer();
  const started = useRef(false);
  const loginRef = useRef(login);
  loginRef.current = login;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sid = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    const next = sessionStorage.getItem("vw_customer_next") || "/concorsi";
    api.customerGoogle(sid)
      .then((r) => { loginRef.current(r); window.history.replaceState(null, "", next); navigate(next, { replace: true }); })
      .catch((e) => { toast.error(err(e, "Accesso Google non riuscito")); window.history.replaceState(null, "", "/login?tab=cliente"); navigate("/login?tab=cliente", { replace: true }); });
  }, [location.hash, navigate]);
  return <div className="min-h-screen bg-background grid place-items-center text-muted-foreground">Accesso in corso…</div>;
}
